import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { TOP_ONLY_SHADOW_CLASS_NAME } from '@avanta/shared/constants/styles';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddEmployeeForm } from 'components/AddEmployeeForm';
import { StepSection } from 'pages/AppointmentsReserve/components/ui/StepSection';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData, type StepProps } from 'pages/AppointmentsReserve/types';
import { resetAvailabilitySelection } from 'pages/AppointmentsReserve/utils';
import { type Employee } from 'types/employees';
import { PatientSearchResults } from './components/PatientSearchResults';
import { SelectedPatientView } from './components/SelectedPatientView';
import { useFetchPatients } from './useFetchPatients';

export const SearchPatientStep = ({ onNext }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { control, setValue } = useFormContext<AppointmentsReserveFormData>();
  const [search, setSearch] = useState('');
  const [showAddNewPatient, setShowAddNewPatient] = useState(false);

  const [selectedPatient, flow] = useWatch({
    control,
    name: [AppointmentsReservePayload.patient, AppointmentsReservePayload.flow],
  });
  const isPatientSelected = Boolean(selectedPatient);

  const debouncedSearch = useDebounce(search);
  const patientSearch = !showAddNewPatient && !isPatientSelected ? debouncedSearch : '';
  const { data: patients, isLoading } = useFetchPatients(patientSearch);

  const listRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(
    listRef,
    'nearestScrollParent',
    patients
  );
  const showScrollShadow = hasOverflow && !isAtBottom;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value);
  };

  const handleSelectPatient = (patient: Employee): void => {
    if (patient.id !== selectedPatient?.id) {
      resetAvailabilitySelection(setValue, flow);
    }
    setSearch(patient.email);
    setValue(AppointmentsReservePayload.patient, patient);
    onNext();
  };

  const handleClearPatient = (): void => {
    resetAvailabilitySelection(setValue, flow);
    setSearch('');
    setValue(AppointmentsReservePayload.patient, undefined);
  };

  const handleAddNewPatient = (): void => {
    setShowAddNewPatient(true);
  };

  const handlePatientCreated = (employee: Employee): void => {
    resetAvailabilitySelection(setValue, flow);
    setSearch(employee.email);
    setValue(AppointmentsReservePayload.patient, employee);
    setShowAddNewPatient(false);
  };

  if (showAddNewPatient) {
    return (
      <AddEmployeeForm
        mode="embedded"
        onSuccess={handlePatientCreated}
        buttons={{
          cancel: {
            onClick: () => setShowAddNewPatient(false),
            variant: 'terciary',
            icon: 'back',
            children: (
              <>
                <ChevronLeft className="size-6" strokeWidth={0.75} />
                {t('Common.back', { ns: LOCIZE_NS.COMMON })}
              </>
            ),
          },
          submit: {
            children: (
              <>
                {t('AppointmentsReserve.SearchPatient.next')}
                <ChevronRight className="size-6" strokeWidth={0.75} />
              </>
            ),
            icon: 'trailing',
            className: 'min-w-64 w-fit',
            variant: 'primary',
          },
        }}
      />
    );
  }

  if (selectedPatient) {
    return (
      <div className="flex w-xl flex-col gap-6">
        <SelectedPatientView
          patient={selectedPatient}
          onBack={handleClearPatient}
          onNext={onNext}
        />
      </div>
    );
  }

  return (
    <div className="w-xl flex-1">
      <StepSection
        title={t(`AppointmentsReserve.SearchPatient.title`)}
        className="flex flex-col gap-5"
        containerClassName="w-full"
      >
        <Input
          value={search}
          aria-label={t(`AppointmentsReserve.SearchPatient.input.placeholder`)}
          placeholder={t(`AppointmentsReserve.SearchPatient.input.placeholder`)}
          leftIcon={Search}
          onChange={handleSearchChange}
          className="w-full"
        />
        {patients && (
          <>
            <div ref={listRef} className="flex flex-col gap-4">
              <PatientSearchResults
                patients={patients}
                isLoading={isLoading}
                onPatientSelect={handleSelectPatient}
              />
            </div>
            <div
              className={cn(
                'sticky bottom-0 z-10 w-xl bg-white pt-6',
                showScrollShadow && TOP_ONLY_SHADOW_CLASS_NAME
              )}
            >
              <div className="relative z-20">
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  icon="leading"
                  onClick={handleAddNewPatient}
                >
                  <Plus className="size-6" />
                  {t(`AppointmentsReserve.SearchPatient.addNewPatient`)}
                </Button>
              </div>
            </div>
          </>
        )}
      </StepSection>
    </div>
  );
};
